import React from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

// styles
import styles from './styles-mobile.module.css';
// config
import {
  ELEMENT_TYPE,
  THEME,
  NAVIGATION_FROM,
  YEAR_ANIMATIONS_DIRECTION
} from '../config/constants';
// data
import timelineData from '../data/timeline';
// local components
import YearComponent from './components-mobile/YearComponent';
import Moment from './components-mobile/MomentComponent';
import Designer from './components-mobile/DesignersComponent';
import Product from './components-mobile/ProductComponent';
import Menu from "./components-mobile/MenuComponent";
import Room from "./components-mobile/RoomComponent";
import RoomPreview from "./components-mobile/RoomPreviewComponent";
import DiscoverComponent from './components-mobile/DiscoverComponent';
import DesignComponent from './components-mobile/DesignComponent';

import HeaderBraun from '../common/HeaderBraun';

// spa loader
import ComponentLoader from '../SpaComponentLoader';

export default class Timeline extends React.Component {

  constructor(props) {
    super(props);
    // load component in SPA mode based on URL
    this.routeConfig = this.props.match;

    this.enableMarkers = false;
    this.pinnedDesigners = [];
    this.designersPerYear = {};

    this._timelineData = timelineData;
    // this._timelineData = timelineData.reverse();

    this.availableYearsArray = this._timelineData
    .filter(timelineElem => timelineElem.type === ELEMENT_TYPE.YEAR)
    .map(yearData => parseInt(yearData.year, 10))
    .sort();

    this.state = {
      showSpaComponentLoader: false,
      currentComponentType: null,
      currentComponentData: null,
    };
  }

  closestEndYear(year) {
    return this.availableYearsArray.reduce((a, b) => {
      return Math.abs(b - year) < Math.abs(a - year) ? b : a;
    });
  }

  navigateToYear(year) {
    gsap.to('.timelineWrapper', { duration: 2, scrollTo: {x: `#year-${year}`, offsetX: 150 } });
  }

  handleMenu(isOppened) {
    gsap.to('.timelineWrapper', { yPercent: !isOppened ? '-100' : 0, ease: 'power1.out', duration: .5 });
  }

  openComponentInSpaMode(type, data) {
    // show component in SPA mode
    this.setState({
      currentComponentType: type,
      currentComponentData: data,
      showSpaComponentLoader: true,
    });
  }

  animateYears() {
    // animate years
    gsap.utils.toArray('.year-elem').forEach(year => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          scroller: '.timelineWrapper',
          trigger: year,
          toggleActions: 'play reverse play reverse',
          start: 'center 95%',
          end: 'center 15%',
          markers: false,
          id: year.getAttribute('id'),
          onEnter: () => year.classList.add(YEAR_ANIMATIONS_DIRECTION.FORWARD),
          onEnterBack: () => year.classList.add(YEAR_ANIMATIONS_DIRECTION.BACKWARD),
          onLeave: () => year.classList.remove(YEAR_ANIMATIONS_DIRECTION.FORWARD, YEAR_ANIMATIONS_DIRECTION.BACKWARD),
          onLeaveBack: () => year.classList.remove(YEAR_ANIMATIONS_DIRECTION.FORWARD, YEAR_ANIMATIONS_DIRECTION.BACKWARD),
        }
      });
      // year number
      timeline.from(year.querySelector('.yearNumber'), { marginRight: '15vw', autoAlpha: 0, scale: .4, duration: 0.2, transformOrigin: 'center right', ease: 'power2.inOut' }, 0);
      // year icon
      timeline.from(year.querySelector('.yearTimelineIcon'), { scale: .8, duration: 0.2, transformOrigin: 'center center', ease: 'power2.inOut' }, 0);
    });
  }

  animateMoments() {
    // animate moments
    gsap.utils.toArray('.moment-elem').forEach(moment => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          scroller: '.timelineWrapper',
          trigger: moment,
          toggleActions: 'play reverse play reverse',
          start: 'center 85%',
          end: 'center 15%',
          markers: false,
          id: moment.getAttribute('id'),
        }
      });

      //  moments & designers & room desc
      timeline.from(moment.querySelector('.desc'), { autoAlpha: 0, duration: .2, scale: .4, transformOrigin: 'center right', ease: 'power2.inOut' }, 0);
      //  moments & designers & room icon
      timeline.from(moment.querySelector('.icon'), { scale: .4, duration: .2, transformOrigin: '46% center', ease: 'power2.inOut' }, 0);
    });
  }

  animateRooms() {
    // animate moments
    gsap.utils.toArray('.room-elem').forEach(moment => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          scroller: '.timelineWrapper',
          trigger: moment,
          toggleActions: 'play reverse play reverse',
          start: 'center 85%',
          end: 'center 15%',
          markers: false,
          id: moment.getAttribute('id'),
        }
      });

      //  moments & designers & room desc
      timeline.from(moment.querySelector('.desc'), { autoAlpha: 0, duration: .2, scale: .4, transformOrigin: 'center right', ease: 'power2.inOut' }, 0);
      //  moments & designers & room icon
      timeline.from(moment.querySelector('.icon'), { scale: .4, duration: .2, transformOrigin: '46% center', ease: 'power2.inOut' }, 0);
    });
  }

  animateProducts() {
    // animate products
    gsap.utils.toArray('.product-elem').forEach(product => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          scroller: '.timelineWrapper',
          trigger: product,
          toggleActions: 'play reverse play reverse',
          start: 'center 85%',
          end: 'center 15%',
          markers: false,
          id: product.getAttribute('id'),
        }
      });
      // product item
      // it there are 2 products
      if (product.querySelector('.item_2')) {
        timeline.from(product.querySelector('.item_1'), {
          scale: .6,
          x: product.querySelector('.item_2').getBoundingClientRect().width - 10,
          y: '-1.6vw',
          duration: 0.2,
          stagger: 0.1,
          ease: 'power2.inOut'
        }, 0);
      
        timeline.from(product.querySelector('.item_2'), {
          scale: .6,
          x: '8vw',
          y: '-1.6vw',
          duration: 0.2,
          stagger: 0.1,
          ease: 'power2.inOut'
        }, 0);
      } else {
        timeline.from(product.querySelector('.item_1'), {
          scale: .6,
          x: '8vw',
          y: '-1.6vw',
          duration: 0.2,
          stagger: 0.1,
          ease: 'power2.inOut'
        }, 0);
      }
      timeline.from(product.querySelectorAll('.item-text'), {
        scale: .6, duration: 0.2, autoAlpha: 0, stagger: 0.1, ease: 'power2.inOut'
      }, 0);
    });
  }

  animateDesigners() {
    // animate moments
    gsap.utils.toArray('.designer-elem').forEach(moment => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          scroller: '.timelineWrapper',
          trigger: moment,
          toggleActions: 'play reverse play reverse',
          start: 'center 85%',
          end: 'center 15%',
          markers: false,
          id: moment.getAttribute('id'),
        }
      });

      //  moments & designers & room desc
      timeline.from(moment.querySelector('.desc'), { autoAlpha: 0, duration: .2, scale: .4, transformOrigin: 'center right', ease: 'power2.inOut' }, 0);
      //  moments & designers & room icon
      timeline.from(moment.querySelector('.icon'), { scale: .4, duration: .2, transformOrigin: '46% center', ease: 'power2.inOut' }, 0);
    })
  }

  animateRoomPreview() {
    // animate room previews
    gsap.utils.toArray('.root-preview-elem').forEach(roomPreview => {
      const roomPreviewId = roomPreview.getAttribute('id');
      const timeline = gsap.timeline({
        scrollTrigger: {
          scroller: '.timelineWrapper',
          trigger: roomPreview,
          toggleActions: 'play reverse play reverse',
          // element viewport (scroller)
          endTrigger: `#ROOM_${roomPreviewId}`,
          start: '40% 70%',
          end: 'bottom+=500% center',
          markers: false,
          id: `ROOM_PREVIEW_${roomPreviewId}`,
        }
      });
      // room preview
      timeline.to(roomPreview, {
        height: '40vh',
        duration: 1,
      });
      timeline.to(roomPreview.querySelector('.room-expand-icon'), {
        autoAlpha: 1,
        scale: 1,
        transformOrigin: 'center center',
        duration: .5,
      })
    });
  }

  animateDesignElements() {
    // animate design elem
    gsap.utils.toArray('.design-elem').forEach(designElem => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          scroller: '.timelineWrapper',
          trigger: designElem,
          // element viewport (scroller)
          start: 'top top',
          end: 'bottom+=25% bottom',
          markers: false,
          scrub: 1, // 1 ?
          id: designElem.getAttribute('id'),
        }
      });
      // step -1
      timeline.set(designElem.querySelector('#useful'), { autoAlpha: 0.25, force3D: true });
      timeline.set(designElem.querySelector('#build'), { autoAlpha: 0.25, force3D: true });

      // step 0
      timeline.to(designElem.querySelector('.sliding-texts'), { marginTop: '-10vw', force3D: true }, 0);
      timeline.to(designElem.querySelector('#simple'), { autoAlpha: 0, }, 0);
      timeline.to(designElem.querySelector('#useful'), { autoAlpha: 1, }, 0);
      timeline.to(designElem.querySelector('#build'), { autoAlpha: 0.25, }, 0);
    
      // step 1
      timeline.to(designElem.querySelector('.sliding-texts'), { marginTop: '-20vw', duration: 1, force3D: true, }, 1);
      timeline.to(designElem.querySelector('#simple'), { autoAlpha: 0, }, 1);
      timeline.to(designElem.querySelector('#useful'), { autoAlpha: 0, }, 1);
      timeline.to(designElem.querySelector('#build'), { autoAlpha: 1, }, 1);
    });
  }

  animateDiscoverElements() {
    // animate design elem
    gsap.utils.toArray('.discover-elem').forEach(designElem => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          scroller: '.timelineWrapper',
          trigger: designElem,
          // element viewport (scroller)
          start: 'top top',
          end: 'bottom+=500% botom',
          markers: false,
          scrub: .5, // 1 ?
          id: designElem.getAttribute('id'),
          force3D: true,
        }
      });
      // step 0
      timeline.to(designElem.querySelector('.discover-elem-1'), { y: '-=100%' }, 0);
      timeline.to(designElem.querySelector('.discover-elem-2'), { y: '-=100%' }, 0);
      timeline.to(designElem.querySelector('.discover-elem-3'), { y: '-=100%' }, 0);

      timeline.to(designElem.querySelector('.discover-elem-1'), { y: '-=102%' }, 1);
      timeline.to(designElem.querySelector('.discover-elem-2'), { y: '-=102%' }, 1);
      timeline.to(designElem.querySelector('.discover-elem-3'), { y: '-=100%' }, 1);
    });
  }

  scrollToBottom() {
    if (typeof document !== `undefined`) {
      const timelineElem = document.querySelector('.timelineWrapper');
      timelineElem.scrollTop = timelineElem.scrollHeight;
    }
  }

  componentDidMount() {
    gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

    // this.scrollToBottom();
    // [Animations on ScrollTrigger]
    // this.animateRoomPreview();
    this.animateYears();
    this.animateMoments();
    this.animateProducts();
    this.animateDesigners();
    this.animateRooms();
    this.animateDesignElements();
    this.animateDiscoverElements();
  }

  showDefaultRoomDetails() {
    const rooms = require(`../data/rooms`).default;
    this.openComponentInSpaMode(ELEMENT_TYPE.ROOM, rooms[2040]);
  }

  render() {
    return (
    <>
    {/* loads compoenents in SPA mode, no Router */}
    {this.state.showSpaComponentLoader &&
    <ComponentLoader
      type={this.state.currentComponentType}
      data={this.state.currentComponentData}
      classesToHide={['.timeline', '.timeline-menu', '.timeline-header', '.timeline-bar']}
      onExitCompleted={() => {
        this.setState({ showSpaComponentLoader: false });
      }}
      from={NAVIGATION_FROM.TIMELINE}
    />}
    <HeaderBraun
      className='timeline-header'
      theme={THEME.BLACK}
    />
    {/* timeline bar */}
    <div className={`timeline-bar ${styles.timelineBar}`}></div>
    {/* timeline wrapper */}
    <div className={`timelineWrapper ${styles.timelineWrapper}`}>
      {/* timeline */}
      <div className={`timeline ${styles.timeline}`}>
        {/* timeline elements */}
        {// eslint-disable-next-line array-callback-return
        this._timelineData && this._timelineData.map((element, index) => {
          switch (element?.type) {
            case ELEMENT_TYPE.DUMMY:
              return (
              <div
                key={index}
                style={{
                  float: 'left',
                  width: '1px',
                  height: '1px',
                }}
                className={styles.timelineElemenent}
              />)
              ;
            case ELEMENT_TYPE.YEAR:
              return (
                <YearComponent
                  key={index}
                  className={`${styles.timelineElemenent} year-elem`}
                  yearClassName='yearNumber'
                  yearIconClassName='yearTimelineIcon'
                  year={element?.year}
                />
              );
            case ELEMENT_TYPE.PRODUCT:
            case ELEMENT_TYPE.PRODUCT_GROUP:
              return (
                <Product
                  key={index}
                  className={`${styles.timelineElemenent} product-elem`}
                  productClassName='item'
                  productTextClassName='item-text'
                  productTimelineIconClassName='icon'
                  goToProductDetail={(data) => {
                    // show component in SPA mode
                    this.openComponentInSpaMode(ELEMENT_TYPE.PRODUCT, data);
                  }}
                  type={ELEMENT_TYPE.PRODUCT}
                  products = {
                    element?.products ? element.products : [{...element}]
                  }
                />
              );
            case ELEMENT_TYPE.DESIGNER:
              return (
                <Designer
                  key={index}
                  className={`${styles.timelineElemenent} designer-elem`}
                  iconClassName='icon'
                  descriptionClassName='desc'
                  icon={element?.image}
                  id={element?.id}
                  goToDesignerDetail={() => {
                    // show component in SPA mode
                    this.openComponentInSpaMode(ELEMENT_TYPE.DESIGNER, element?.data);
                  }}
                  title={element?.name}
                  subtitle={element?.position}
                  type={ELEMENT_TYPE.DESIGNER}
                  endYear={this.closestEndYear(element?.between[1])}
                />
              );
            case ELEMENT_TYPE.MOMENT:
              return (
                <Moment
                  key={index}
                  id={element?.id}
                  className={`${styles.timelineElemenent} moment-elem`}
                  timelineIconClassName='icon'
                  descriptionClassName='desc'
                  icon={element?.image}
                  goToMomentDetail={() => {
                    // show component in SPA mode
                    this.openComponentInSpaMode(ELEMENT_TYPE.MOMENT, element?.data);
                  }}
                  title={element?.name}
                  type={ELEMENT_TYPE.MOMENT}
                  subtitle={element?.year}
                />
              );
            case ELEMENT_TYPE.ROOM:
              return (
                <Room
                  key={index}
                  id={element?.id}
                  className={`${styles.timelineElemenent} room-elem`}
                  timelineIconClassName='icon'
                  descriptionClassName='desc'
                  title={element?.label}
                  subtitle={element?.action}
                />
              );
            case ELEMENT_TYPE.ROOM_PREVIEW:
              return (
                <RoomPreview
                  key={index}
                  id={element?.id}
                  roomPreviewId={element?.roomPreviewId}
                  url={element?.url}
                  goToRoomDetail={(id) => {
                    // show component in SPA mode
                    this.openComponentInSpaMode(ELEMENT_TYPE.ROOM, element?.data);
                  }}
                  image={element?.image}
                  type={ELEMENT_TYPE.ROOM}
                  className="root-preview-elem"
                  roomExpandIconClassName="room-expand-icon"
                />
              )
            case ELEMENT_TYPE.DESIGN:
              return (
                <DesignComponent
                  key={index}
                  id={element.id}
                  className={`${styles.timelineElemenent} design-elem`}
                  slidingTextsClassName='sliding-texts'
                  design={element}
                />
              );
            case ELEMENT_TYPE.DISCOVER:
              return (
                <DiscoverComponent
                  key={index}
                  className={`${styles.timelineElemenent} discover-elem`}
                  elementClassName={'discover-elem'}
                  title={element?.title}
                  subTitle={element?.subTitle}
                  elements={element?.elements}
                />
              )
            default:
              break;
          }
        })}
      </div>
    </div>
    {/* menu */}
    <Menu
      onClickParent={(menuOpened) => { this.handleMenu(menuOpened) }}
      className='timeline-menu'
      menuWrapperClassName='menu-wrapper'
      onRoomExplorerClicked={() => {this.showDefaultRoomDetails()}}
    />
    </>
  )}
}